'use client';

import { cn } from '@lib/util/cn';
import { Minus, Plus, Spinner } from '@modules/common/icons';

type QuantityStepperProps = {
  value: number;
  max: number;
  isLoading?: boolean;
  onChange: (value: number) => void;
  'aria-label'?: string;
  className?: string;
};

export function QuantityStepper({
  value,
  max,
  isLoading = false,
  onChange,
  'aria-label': ariaLabel,
  className,
}: QuantityStepperProps) {
  return (
    <fieldset
      className={cn(
        'flex items-center gap-1 bg-surface-neutral-subtle rounded-lg px-3 py-2 text-fg-neutral-strong',
        className,
      )}
    >
      <legend className="sr-only">{ariaLabel ?? 'Quantity'}</legend>
      <button
        type="button"
        aria-label="Decrease quantity"
        disabled={isLoading || value <= 1}
        onClick={() => onChange(value - 1)}
        className="leading-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Minus size="14" aria-hidden="true" />
      </button>
      <span
        className="relative typography-paragraph-xs w-6 text-center select-none"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className={isLoading ? 'invisible' : undefined}>{value}</span>
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner size="14" aria-label="Updating quantity" />
          </span>
        )}
      </span>
      <button
        type="button"
        aria-label="Increase quantity"
        disabled={isLoading || value >= max}
        onClick={() => onChange(value + 1)}
        className="leading-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus size="14" aria-hidden="true" />
      </button>
    </fieldset>
  );
}
